<template>
  <div class="gradient-picker">
    <label class="label block mb-2">{{ $t('Header background') }}</label>

    <!-- Mode Toggle -->
    <div class="gp-toggle">
      <button
        v-for="opt in modes"
        :key="opt.value"
        type="button"
        class="gp-toggle__btn"
        :class="{ 'gp-toggle__btn--active': mode === opt.value }"
        @click="mode = opt.value"
      >
        {{ $t(opt.label) }}
      </button>
    </div>

    <!-- Solid Mode -->
    <transition name="gp-fade">
      <div v-if="mode === 'solid'" class="gp-panel">
        <div class="gp-preview" :style="{ background: solidColor }"></div>
        <div class="gp-row" style="margin-top: 12px">
          <SwatchColorPicker v-model="solidColor" dropdown-position="left" />
          <span class="gp-hex">{{ solidColor }}</span>
        </div>
      </div>
    </transition>

    <!-- Gradient Mode -->
    <transition name="gp-fade">
      <div v-if="mode === 'gradient'" class="gp-panel">
        <!-- Gradient Bar -->
        <div class="gp-bar-container">
          <div
            ref="gradientBar"
            class="gp-bar"
            :style="{ background: cssValue }"
            @click="addStopAtClick"
          >
            <div
              v-for="stop in sortedStops"
              :key="stop.id"
              class="gp-stop"
              :class="{ 'gp-stop--active': selectedStopId === stop.id }"
              :style="{ left: stop.position + '%' }"
              @mousedown.prevent.stop="startStopDrag($event, stop)"
              @touchstart.prevent.stop="startStopDrag($event, stop)"
              @click.stop="selectedStopId = stop.id"
            >
              <div class="gp-stop__knob" :style="{ backgroundColor: stop.color }"></div>
            </div>
          </div>
        </div>

        <!-- Controls Grid -->
        <div class="gp-grid">
          <!-- Left: selected stop color + position -->
          <div class="gp-grid__left">
            <div v-if="selectedStop" class="gp-row">
              <SwatchColorPicker v-model="selectedStop.color" dropdown-position="left" />
              <span class="gp-hex">{{ selectedStop.color }}</span>
              <div class="gp-input-group">
                <input
                  type="number"
                  class="gp-input gp-input--sm"
                  :value="Math.round(selectedStop.position)"
                  @input="updateStopPosition($event)"
                  min="0"
                  max="100"
                />
                <span class="gp-input-group__suffix">%</span>
              </div>
              <button
                v-if="stops.length > 2"
                type="button"
                class="gp-icon-btn gp-icon-btn--danger"
                @click="removeStop(selectedStop.id)"
                :title="$t('Remove color stop')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Right: type + angle -->
          <div class="gp-grid__right">
            <div class="gp-type-toggle">
              <button
                type="button"
                class="gp-type-btn"
                :class="{ 'gp-type-btn--active': gradientType === 'linear' }"
                @click="gradientType = 'linear'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="gp-type-icon">
                  <path d="M3.75 2a.75.75 0 0 1 .75.75v10.5a.75.75 0 0 1-1.5 0V2.75A.75.75 0 0 1 3.75 2Zm8.5 0a.75.75 0 0 1 .75.75v10.5a.75.75 0 0 1-1.5 0V2.75a.75.75 0 0 1 .75-.75ZM6 8a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5A.75.75 0 0 1 6 8Z" />
                </svg>
              </button>
              <button
                type="button"
                class="gp-type-btn"
                :class="{ 'gp-type-btn--active': gradientType === 'radial' }"
                @click="gradientType = 'radial'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="gp-type-icon">
                  <path fill-rule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm0-1.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Zm0-2.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <div v-if="gradientType === 'linear'" class="gp-row">
              <div
                class="gp-dial"
                @mousedown.prevent="startAngleDrag"
                @touchstart.prevent="startAngleDrag"
              >
                <div class="gp-dial__needle" :style="{ transform: 'rotate(' + angle + 'deg)' }"></div>
              </div>
              <div class="gp-input-group">
                <input
                  type="number"
                  class="gp-input gp-input--sm"
                  v-model.number="angle"
                  min="0"
                  max="360"
                  @input="clampAngle"
                />
                <span class="gp-input-group__suffix">°</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Presets -->
        <div class="gp-presets">
          <span class="gp-presets__label">{{ $t('Presets') }}</span>
          <div class="gp-presets__row">
            <button
              v-for="preset in presets"
              :key="preset.name"
              type="button"
              class="gp-preset"
              :style="{ background: presetCss(preset) }"
              :title="preset.name"
              @click="applyPreset(preset)"
            ></button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Hidden Inputs -->
    <input type="hidden" :name="inputNameType" :value="mode" />
    <input type="hidden" :name="inputNameValue" :value="mode === 'none' ? '' : cssValue" />
  </div>
</template>

<script>
import SwatchColorPicker from './swatch-color-picker.vue'

let stopIdCounter = 0

const PRESETS = [
  { name: 'Sunset',   stops: [{ color: '#f97316', position: 0 }, { color: '#ec4899', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Ocean',    stops: [{ color: '#06b6d4', position: 0 }, { color: '#3b82f6', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Forest',   stops: [{ color: '#22c55e', position: 0 }, { color: '#0d9488', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Lavender', stops: [{ color: '#8b5cf6', position: 0 }, { color: '#ec4899', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Midnight', stops: [{ color: '#1e293b', position: 0 }, { color: '#334155', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Ember',    stops: [{ color: '#dc2626', position: 0 }, { color: '#f59e0b', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Sky',      stops: [{ color: '#38bdf8', position: 0 }, { color: '#818cf8', position: 50 }, { color: '#c084fc', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Aurora',   stops: [{ color: '#22d3ee', position: 0 }, { color: '#a78bfa', position: 50 }, { color: '#fb7185', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Dusk',     stops: [{ color: '#1e3a5f', position: 0 }, { color: '#4c1d95', position: 50 }, { color: '#831843', position: 100 }], angle: 135, type: 'linear' },
  { name: 'Peach',    stops: [{ color: '#fdba74', position: 0 }, { color: '#f472b6', position: 100 }], angle: 135, type: 'linear' },
]

function nextStopId() {
  return ++stopIdCounter
}

function parseGradientString(str) {
  if (!str) return null
  const linearMatch = str.match(/^linear-gradient\(\s*([\d.]+)deg\s*,\s*(.+)\)$/i)
  if (linearMatch) {
    return { type: 'linear', angle: parseFloat(linearMatch[1]), stops: parseColorStops(linearMatch[2]) }
  }
  const radialMatch = str.match(/^radial-gradient\(\s*circle\s*,\s*(.+)\)$/i)
  if (radialMatch) {
    return { type: 'radial', angle: 135, stops: parseColorStops(radialMatch[1]) }
  }
  return null
}

function parseColorStops(stopsStr) {
  const stops = []
  const parts = stopsStr.split(/,(?![^(]*\))/).map(s => s.trim())
  for (const part of parts) {
    const match = part.match(/^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))\s+([\d.]+)%$/)
    if (match) {
      stops.push({ id: nextStopId(), color: match[1], position: parseFloat(match[2]) })
    }
  }
  return stops.length >= 2 ? stops : null
}

export default {
  name: 'GradientPicker',
  components: { SwatchColorPicker },

  props: {
    bgType:         { type: String, default: 'none' },
    bgValue:        { type: String, default: '' },
    inputNameType:  { type: String, required: true },
    inputNameValue: { type: String, required: true }
  },

  data() {
    const initial = this.parseInitialValue()
    return {
      mode: initial.mode,
      solidColor: initial.solidColor,
      stops: initial.stops,
      angle: initial.angle,
      gradientType: initial.gradientType,
      selectedStopId: initial.stops[0]?.id || null,
      draggingStop: null,
      modes: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'gradient', label: 'Gradient' }
      ],
      presets: PRESETS
    }
  },

  computed: {
    sortedStops() {
      return [...this.stops].sort((a, b) => a.position - b.position)
    },
    selectedStop() {
      return this.stops.find(s => s.id === this.selectedStopId) || null
    },
    cssValue() {
      if (this.mode === 'solid') return this.solidColor
      if (this.mode === 'gradient') return this.buildGradientCss()
      return ''
    },
  },

  methods: {
    parseInitialValue() {
      const mode = this.bgType || 'none'
      const val = this.bgValue || ''
      const defaults = {
        mode,
        solidColor: '#2D80FA',
        stops: [
          { id: nextStopId(), color: '#4F46E5', position: 0 },
          { id: nextStopId(), color: '#7C3AED', position: 100 }
        ],
        angle: 135,
        gradientType: 'linear'
      }
      if (mode === 'solid' && val) {
        defaults.solidColor = val
      } else if (mode === 'gradient' && val) {
        const parsed = parseGradientString(val)
        if (parsed) {
          defaults.stops = parsed.stops
          defaults.angle = parsed.angle
          defaults.gradientType = parsed.type
        }
      }
      return defaults
    },

    buildGradientCss() {
      const sorted = this.sortedStops
      const stopsStr = sorted.map(s => `${s.color} ${Math.round(s.position)}%`).join(', ')
      if (this.gradientType === 'radial') return `radial-gradient(circle, ${stopsStr})`
      return `linear-gradient(${Math.round(this.angle)}deg, ${stopsStr})`
    },

    presetCss(preset) {
      const stopsStr = preset.stops.map(s => `${s.color} ${s.position}%`).join(', ')
      if (preset.type === 'radial') return `radial-gradient(circle, ${stopsStr})`
      return `linear-gradient(${preset.angle}deg, ${stopsStr})`
    },

    applyPreset(preset) {
      this.gradientType = preset.type
      this.angle = preset.angle
      this.stops = preset.stops.map(s => ({ id: nextStopId(), color: s.color, position: s.position }))
      this.selectedStopId = this.stops[0].id
    },

    startStopDrag(event, stop) {
      this.selectedStopId = stop.id
      this.draggingStop = stop
      const onMove = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        const bar = this.$refs.gradientBar
        if (!bar) return
        const rect = bar.getBoundingClientRect()
        let pct = ((clientX - rect.left) / rect.width) * 100
        this.draggingStop.position = Math.max(0, Math.min(100, pct))
      }
      const onUp = () => {
        this.draggingStop = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend', onUp)
    },

    addStopAtClick(event) {
      const bar = this.$refs.gradientBar
      if (!bar) return
      const rect = bar.getBoundingClientRect()
      const pct = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100))
      const sorted = this.sortedStops
      let color = '#888888'
      for (let i = 0; i < sorted.length - 1; i++) {
        if (pct >= sorted[i].position && pct <= sorted[i + 1].position) {
          const ratio = (pct - sorted[i].position) / (sorted[i + 1].position - sorted[i].position || 1)
          color = this.interpolateColor(sorted[i].color, sorted[i + 1].color, ratio)
          break
        }
      }
      const newStop = { id: nextStopId(), color, position: pct }
      this.stops.push(newStop)
      this.selectedStopId = newStop.id
    },

    removeStop(id) {
      if (this.stops.length <= 2) return
      const idx = this.stops.findIndex(s => s.id === id)
      this.stops.splice(idx, 1)
      if (this.selectedStopId === id) {
        this.selectedStopId = this.stops[0]?.id || null
      }
    },

    updateStopPosition(event) {
      if (!this.selectedStop) return
      let val = parseInt(event.target.value, 10)
      if (isNaN(val)) return
      this.selectedStop.position = Math.max(0, Math.min(100, val))
    },

    startAngleDrag(event) {
      const el = event.currentTarget
      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const calcAngle = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        const clientY = e.touches ? e.touches[0].clientY : e.clientY
        let deg = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI) + 90
        if (deg < 0) deg += 360
        this.angle = Math.round(deg)
      }
      calcAngle(event)
      const onMove = (e) => calcAngle(e)
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend', onUp)
    },

    clampAngle() {
      if (this.angle < 0) this.angle = 0
      if (this.angle > 360) this.angle = 360
    },

    interpolateColor(hex1, hex2, ratio) {
      const r1 = parseInt(hex1.slice(1, 3), 16), g1 = parseInt(hex1.slice(3, 5), 16), b1 = parseInt(hex1.slice(5, 7), 16)
      const r2 = parseInt(hex2.slice(1, 3), 16), g2 = parseInt(hex2.slice(3, 5), 16), b2 = parseInt(hex2.slice(5, 7), 16)
      const r = Math.round(r1 + (r2 - r1) * ratio), g = Math.round(g1 + (g2 - g1) * ratio), b = Math.round(b1 + (b2 - b1) * ratio)
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
/* ==============================
   MODE TOGGLE
   ============================== */
.gp-toggle {
  /* Matches the shared tab_group.vue pill-tab pattern */
  display: inline-flex;
  align-items: center;
  border-radius: 0.5rem;
  padding: 4px;
  gap: 2px;
  margin-bottom: 12px;
  background: #f0f0f0;
}

.gp-toggle__btn {
  padding: 6px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #a6a6a6;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;
}

.gp-toggle__btn:hover {
  color: #262626;
  white-space: nowrap;
}

.gp-toggle__btn--active {
  color: #262626;
  background: white;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.gp-toggle__btn:hover:not(.gp-toggle__btn--active) {
  color: #64748b;
}

/* ==============================
   PANEL
   ============================== */
.gp-panel {
  background: white;
  border: 1px solid #c9d4df;
  border-radius: 0.5rem;
  padding: 14px;
}

.gp-fade-enter-active,
.gp-fade-leave-active {
  transition: opacity 150ms ease;
}
.gp-fade-enter,
.gp-fade-leave-to {
  opacity: 0;
}

/* ==============================
   SHARED ROW / HEX / INPUTS
   ============================== */
.gp-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gp-hex {
  font-size: 12px;
  color: #64748b;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: -0.01em;
}

.gp-input-group {
  display: flex;
  align-items: center;
  border: 1px solid #c9d4df;
  border-radius: 0.375rem;
  background: white;
}

.gp-input-group__suffix {
  padding: 0 8px 0 0;
  font-size: 12px;
  color: #64748b;
  pointer-events: none;
  flex-shrink: 0;
}

.gp-input {
  border: none;
  outline: none;
  font-size: 12px;
  color: #334155;
  background: transparent;
}

.gp-input--sm {
  width: 54px;
  padding: 4px 4px 4px 8px;
  text-align: right;
}

.gp-input:focus {
  outline: none;
}

/* ==============================
   PREVIEW (Solid mode)
   ============================== */
.gp-preview {
  height: 40px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* ==============================
   GRADIENT BAR
   ============================== */
.gp-bar-container {
  margin-bottom: 20px;
  padding: 0 8px;
}

.gp-bar {
  height: 40px;
  border-radius: 6px;
  position: relative;
  cursor: crosshair;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* ==============================
   COLOR STOPS
   ============================== */
.gp-stop {
  position: absolute;
  bottom: -8px;
  transform: translateX(-50%);
  cursor: grab;
  z-index: 1;
}

.gp-stop:active {
  cursor: grabbing;
}

.gp-stop--active {
  z-index: 2;
}

.gp-stop__knob {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 100ms ease, box-shadow 100ms ease;
}

.gp-stop--active .gp-stop__knob {
  transform: scale(1.25);
  box-shadow: 0 0 0 1.5px #334155, 0 1px 4px rgba(0, 0, 0, 0.2);
}

/* ==============================
   CONTROLS GRID
   ============================== */
.gp-grid {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.gp-grid__left {
  display: flex;
  align-items: center;
}

.gp-grid__right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/* ==============================
   TYPE TOGGLE (Linear/Radial)
   ============================== */
.gp-type-toggle {
  display: inline-flex;
  background: #fafafa;
  border: 1px solid #c9d4df;
  border-radius: 0.375rem;
  padding: 2px;
  gap: 2px;
}

.gp-type-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 26px;
  border-radius: 5px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 150ms ease;
}

.gp-type-btn:hover {
  color: #64748b;
}

.gp-type-btn--active {
  background: white;
  color: #1e293b;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.gp-type-icon {
  width: 14px;
  height: 14px;
}

/* ==============================
   ANGLE DIAL
   ============================== */
.gp-dial {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  background: white;
  transition: border-color 150ms ease;
}

.gp-dial:hover {
  border-color: #94a3b8;
}

.gp-dial__needle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: 45%;
  background: #334155;
  border-radius: 1px;
  transform-origin: 50% 0%;
}

/* ==============================
   REMOVE STOP BUTTON
   ============================== */
.gp-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  color: #cbd5e1;
  transition: all 120ms ease;
  padding: 0;
  flex-shrink: 0;
}

.gp-icon-btn svg {
  width: 14px;
  height: 14px;
}

.gp-icon-btn--danger:hover {
  color: #ef4444;
  background: #fef2f2;
}

/* ==============================
   PRESETS
   ============================== */
.gp-presets {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eef1f5;
}

.gp-presets__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  display: block;
  margin-bottom: 8px;
}

.gp-presets__row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.gp-preset {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: transform 100ms ease, border-color 100ms ease;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  padding: 0;
}

.gp-preset:hover {
  transform: scale(1.12);
  border-color: #64748b;
}
</style>
